import "typeface-roboto-mono";
import { Suspense, useEffect } from "react";
import { BrowserRouter, Route, Navigate, Routes } from "react-router";

import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import * as Sentry from "@sentry/react";

import { ErrorFallback } from "./components/2/ErrorFallback";
import { ReactQueryWrapper } from "./components/2/react-query";
import { Shell as Shell2, ShellWithSidebar as Shell2WithSidebar } from "./components/2/Shell";
import { AppConfig } from "./components/app-config";
import { Shell } from "./components/Shell";
import { usePermerRequirements } from "./hooks/useHasPermission";
import { Agencies } from "./subapps/2/geographies/agencies";
import { PostalCodes } from "./subapps/2/geographies/postal-codes";
import { PostalCodesImport } from "./subapps/2/geographies/postal-codes/import";
import { Regions } from "./subapps/2/geographies/regions";
import { Zones } from "./subapps/2/geographies/zones";
import { Hohishes } from "./subapps/2/hohishes";
import { EditIdentityHohish } from "./subapps/2/hohishes/identity/edit";
import { IdentityHohish } from "./subapps/2/hohishes/identity/index";
import { CreatePostingHohish, EditPostingHohish } from "./subapps/2/hohishes/postings/create-edit";
import { PostingsHohish } from "./subapps/2/hohishes/postings/index";
import { SingleLayoutHohish } from "./subapps/2/hohishes/single-layout";
import { ViewHohish } from "./subapps/2/hohishes/view";
import { Hub } from "./subapps/2/hub";
import { MfaAircall } from "./subapps/2/mfa-aircall";
import { NotFound } from "./subapps/2/not-found";
import { Self } from "./subapps/2/self";
import { TestError } from "./subapps/2/test-error";
import { ThirdPartiesErrors } from "./subapps/2/third-parties-errors";
import { CreateFinancialHelp } from "./subapps/financial-help/create-financial-help";
import { FinancialHelpTable } from "./subapps/financial-help/financial-help-table";
import { WaitingForNigol } from "./subapps/loginish";

const AppInner = () => {
  const permer = usePermerRequirements();
  const hasAdminGeneral = permer.hasDsitAdminGeneral();
  const hasAdminDsi = permer.hasDsitAdminDsi();
  const canEditAdvancedHohishFields = permer.hasDsitUserEditAdvanced();
  const canEditPostalCodes = permer.hasDsitGeographyPostalCodesEdit();
  const canEditFinancialHelps = permer.hasDsitFinancialHelpsEdit();
  const isTeamDSIT =
    AppConfig.publicClientApplication.getActiveAccount()?.username === "bastien@ouihelp.fr";

  const defaultRedirectTo = hasAdminGeneral ? "/2/hub" : "/2/self";

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/2" element={<Shell2 />}>
          <Route path="" element={<Navigate replace to={defaultRedirectTo} />} />

          <Route path="self" element={<Self />} />
          <Route path="mfa-aircall" element={<MfaAircall />} />

          {hasAdminGeneral && (
            <>
              <Route path="hub" element={<Hub />} />

              <Route path="admin">
                <Route path="" element={<Shell2WithSidebar />}>
                  <Route path="hohishes" element={<Hohishes />} />
                  <Route path="hohishes/:hohishUuid" element={<SingleLayoutHohish />}>
                    <Route index element={<ViewHohish />} />
                    <Route path="identity" element={<IdentityHohish />} />
                    {canEditAdvancedHohishFields && (
                      <Route path="identity/edit" element={<EditIdentityHohish />} />
                    )}
                    <Route path="postings" element={<PostingsHohish />} />
                    <Route path="postings/create" element={<CreatePostingHohish />} />
                    <Route path="postings/:postingUuid/edit" element={<EditPostingHohish />} />
                  </Route>

                  <Route path="geographies">
                    <Route path="regions" element={<Regions />} />
                    <Route path="zones" element={<Zones />} />
                    <Route path="agencies" element={<Agencies />} />
                    <Route path="postal-codes" element={<PostalCodes />} />
                    {canEditPostalCodes && (
                      <Route path="postal-codes/import" element={<PostalCodesImport />} />
                    )}
                  </Route>

                  {hasAdminDsi && (
                    <Route path="dsi">
                      <Route path="third-parties-errors" element={<ThirdPartiesErrors />} />
                    </Route>
                  )}

                  {isTeamDSIT && <Route path="test-error" element={<TestError />} />}
                </Route>
              </Route>
            </>
          )}
        </Route>

        <Route path="/1" element={<Shell />}>
          <Route path="financial-helps" element={<FinancialHelpTable />} />
          {canEditFinancialHelps && (
            <Route path="financial-helps/create" element={<CreateFinancialHelp />} />
          )}
        </Route>

        <Route path="/" element={<Navigate replace to={defaultRedirectTo} />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
};

const SentryUserSetter = ({ children }: { children: React.ReactNode }) => {
  const { instance } = useMsal();
  const currentActiveAccount = instance.getActiveAccount();

  if (!currentActiveAccount) {
    throw new Error("No active account found");
  }

  Sentry.setUser({
    id: currentActiveAccount.homeAccountId,
    email: currentActiveAccount.username,
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Sentry.setUser(null);
    };
  }, []);

  return children;
};

export const AppRoot = () => (
  <Sentry.ErrorBoundary fallback={ErrorFallback}>
    <MsalProvider instance={AppConfig.publicClientApplication}>
      <UnauthenticatedTemplate>
        <WaitingForNigol />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <SentryUserSetter>
          <Suspense>
            <ReactQueryWrapper>
              <AppInner />
            </ReactQueryWrapper>
          </Suspense>
        </SentryUserSetter>
      </AuthenticatedTemplate>
    </MsalProvider>
  </Sentry.ErrorBoundary>
);
